/**
 * UserContext: global state for the logged-in user (PrivateUser | null).
 * UserProvider manages it with an immer-based reducer, hydrates from
 * localStorage on load, and persists changes back. Exposes useUserState /
 * useUserDispatch hooks plus helpers (setUserContext, clearUserContext,
 * addPursuitToUser, removePursuitFromUser) used by the API layer,
 * useAuthSync, and the axios 401 handler in lib/axios.ts.
 */
import { createContext, useReducer, ReactNode, useContext, useEffect } from "react";
import { produce } from "immer";
import { PrivateUser } from "@/types/user";

const UserContext = createContext<PrivateUser | null>(null);
const UserDispatchContext = createContext<React.Dispatch<UserAction> | null>(null);

export type UserAction = { type: "SET_USER"; payload: PrivateUser } | { type: "CLEAR_USER" };

export const userReducer = produce((_state: PrivateUser | null, action: UserAction) => {
    switch (action.type) {
        case "SET_USER":
            return action.payload;
        case "CLEAR_USER":
            return null;
        default:
            return _state;
    }
});

export function setUserContext(dispatch: React.Dispatch<UserAction> | null, user: PrivateUser) {
    if (!dispatch) return;
    console.log("Setting user context to", user);
    dispatch({ type: "SET_USER", payload: user });
}

export function clearUserContext(dispatch: React.Dispatch<UserAction> | null) {
    if (!dispatch) return;
    dispatch({ type: "CLEAR_USER" });
}

function initializeUserState() {
    if (typeof window === "undefined") {
        return null;
    }
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
}

export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [user, dispatch] = useReducer(userReducer, null, initializeUserState);

    useEffect(() => {
        if (user) {
            localStorage.setItem("user", JSON.stringify(user));
        } else {
            localStorage.removeItem("user");
        }
    }, [user]);

    return (
        <UserContext.Provider value={user}>
            <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
        </UserContext.Provider>
    );
};

export const useUserState = () => {
    return useContext(UserContext);
};

export const useUserDispatch = () => {
    return useContext(UserDispatchContext);
};

export function addPursuitToUser(
    dispatch: React.Dispatch<UserAction> | null,
    user: PrivateUser | null,
    bountyId: number
) {
    if (!dispatch || !user) return;
    dispatch({
        type: "SET_USER",
        payload: {
            ...user,
            pursuitIds: [...user.pursuitIds, bountyId],
        },
    });
}

export function removePursuitFromUser(
    dispatch: React.Dispatch<UserAction> | null,
    user: PrivateUser | null,
    bountyId: number
) {
    if (!dispatch || !user) return;
    dispatch({
        type: "SET_USER",
        payload: {
            ...user,
            pursuitIds: user.pursuitIds.filter((id) => id !== bountyId),
        },
    });
}
